import SubmitButton from "@pathwright/ui/src/components/button/SubmitButton"
import Indicator from "@pathwright/ui/src/components/indicator/Indicator"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ui/src/components/tooltip/Tooltip"
import PropTypes from "prop-types"
import styled from "styled-components"
// @ts-expect-error missing types
import { TERTIARY_GRAY } from "@pathwright/ui/src/components/utils/colors"
import {
  useCohortSyncPlanContext,
  useCohortSyncPlanQuery
} from "./SyncPlanContext"
import { SYNC_DIR_BACKWARD, SYNC_DIR_FORWARD } from "./constants"
import { SelectedSourceIDs } from "./types"
import {
  getSyncUrl,
  getSyncableChangesCount,
  syncFromSourceCohortPrevented,
  usePathHasBeenPublished,
  useShowSyncOption
} from "./utils"

type SyncButtonProps = {
  cohortId: number
  selectedItemSourceIds: SelectedSourceIDs
  onSync?: () => void
  full?: boolean
  disabled?: boolean
}

const SyncButton = ({
  cohortId,
  selectedItemSourceIds = {
    [SYNC_DIR_FORWARD]: null,
    [SYNC_DIR_BACKWARD]: null
  } as SelectedSourceIDs,
  onSync,
  full,
  disabled: propsDisabled
}: SyncButtonProps) => {
  const { t } = useTranslate()

  const { fromCohort } = useCohortSyncPlanQuery({
    cohortId
  })

  const {
    refetchSyncPlanQueries,
    [SYNC_DIR_FORWARD]: forwardSync,
    [SYNC_DIR_BACKWARD]: backwardSync,
    fromPath
  } = useCohortSyncPlanContext({
    cohortId,
    selectedItemSourceIds
  })

  // We only show (backward) sync status for certain user types (Editor) and
  // if the resource meets certain criteria (multiple Cohorts or a Published Source Path)
  const shouldShowSyncStatus = useShowSyncOption({
    cohortId
  })

  // No longer necessary, at least so we believe.
  // useEffect(() => {
  //   if (forwardSync || backwardSync) {
  //     refetchSyncPlanQueries()
  //   }
  // }, [cohortId])

  // check if the path has ever been published so we can show the correct icon
  const pathHasBeenPublished = usePathHasBeenPublished({
    cohortId
  })
  const hasForwardSyncableChanges = !!getSyncableChangesCount(forwardSync)
  const hasBackwardSyncableChanges = !!getSyncableChangesCount(backwardSync)
  const isPublished = pathHasBeenPublished && !hasForwardSyncableChanges
  const showIsSynced =
    isPublished && shouldShowSyncStatus && !hasBackwardSyncableChanges

  const disabled = propsDisabled || !fromPath?.items?.edges?.length
  const showIndicator =
    !disabled &&
    // only show indicator on main button when there are forward sync changes
    !!hasForwardSyncableChanges &&
    // don't show indicator if this is a forward sync from source but there are no in-progress cohorts to sync to
    !syncFromSourceCohortPrevented(fromCohort)

  // The Sync Button is always used as a Publish (ie, forward sync) button;
  // Backward sync is accessed in other ways
  const label = isPublished ? t("Published") : t("Publish")
  // Show disabled UI without actually disabling button
  const buttonStyles = isPublished
    ? {
        color: TERTIARY_GRAY
      }
    : {}
  const icon = showIsSynced
    ? "cloud-arrows-fill"
    : pathHasBeenPublished
    ? "cloud-fill"
    : "cloud"

  const to = !onSync ? getSyncUrl() : null

  const button = full ? (
    <SubmitButton
      styleType="primary"
      onClick={onSync}
      to={to}
      disabled={disabled}
      inverted
      brand={false}
      style={buttonStyles}
    >
      <>
        {!!icon && (
          <>
            <Pathicon icon={icon} />
            <span> </span>
          </>
        )}
        <TruncatedText>{label}</TruncatedText>
      </>
      {showIndicator && <StyledFullIndicator size=".8em" />}
    </SubmitButton>
  ) : (
    <Tooltip title={label}>
      <SubmitButton
        styleType="blank"
        onClick={onSync}
        to={to}
        disabled={disabled}
        inverted
        brand={false}
        style={buttonStyles}
      >
        {!!icon && <Pathicon icon={icon} />}
        {showIndicator && <StyledIndicator size=".6em" />}
      </SubmitButton>
    </Tooltip>
  )

  return button
}

SyncButton.displayName = "SyncButton"

SyncButton.propTypes = {
  // the id of the cohort to sync from
  cohortId: PropTypes.number.isRequired,
  selectedItemSourceIds: PropTypes.shape({
    [SYNC_DIR_FORWARD]: PropTypes.array,
    [SYNC_DIR_BACKWARD]: PropTypes.array
  }),
  onSync: PropTypes.func,
  // whether to show full button or simple icon
  full: PropTypes.bool,
  // whether to disable the button (always hides indicator)
  disabled: PropTypes.bool
}

const TruncatedText = styled.span`
  max-width: 200px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const StyledFullIndicator = styled(Indicator)`
  position: absolute;
  top: -0.1em;
  right: -0.1em;
`

const StyledIndicator = styled(Indicator)`
  position: absolute;
  top: 0.1em;
  right: 0.7em;
`

export default SyncButton
